import { createRoute, Link, useNavigate } from '@tanstack/react-router'
import { adminRoute } from './route'
import { useCallback, useEffect, useState } from 'react'
import { orpc } from '@/lib/orpc-client'
import { formatCredits } from '@hub/contracts'
import { Button } from '@/components/ui/button'
import { Plus } from 'lucide-react'
import { CreateOrgDialog } from '@/components/admin/create-org-dialog'

type AdminOrg = Awaited<ReturnType<typeof orpc.admin.listOrganizations>>['organizations'][number]

export const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  component: AdminOrgList,
})

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB')
}

function signupModeLabel(mode: string): string {
  switch (mode) {
    case 'invite_only':
      return 'Invite only'
    case 'domain_restricted':
      return 'Domain restricted'
    default:
      return mode
  }
}

function AdminOrgList() {
  const navigate = useNavigate()
  const [orgs, setOrgs] = useState<AdminOrg[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  const loadOrgs = useCallback(async () => {
    try {
      const res = await orpc.admin.listOrganizations()
      setOrgs(res.organizations)
    } catch {
      /* ignore */
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadOrgs()
  }, [loadOrgs])

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Organizations</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage all organizations on the platform.
          </p>
        </div>
        <Button onClick={() => setIsCreateOpen(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Create organization
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : orgs.length === 0 ? (
        <p className="text-sm text-muted-foreground">No organizations yet.</p>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Name</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Slug</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Members</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Projects</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Credits</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Signup</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Created</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((org) => (
                <tr
                  key={org.id}
                  onClick={() =>
                    void navigate({
                      to: '/admin/organizations/$orgId',
                      params: { orgId: org.id },
                    })
                  }
                  className="border-b border-border last:border-b-0 hover:bg-muted/30 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 font-medium text-foreground">
                    {org.name}
                    {!org.isActive && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{org.slug}</td>
                  <td className="px-4 py-3 text-right text-muted-foreground">{org.memberCount}</td>
                  <td className="px-4 py-3 text-right text-muted-foreground">
                    {org.projectCount}/{org.projectLimit}
                  </td>
                  <td className="px-4 py-3 text-right text-muted-foreground tabular-nums">
                    {formatCredits(org.creditsUsed, 'en-US')}/
                    {formatCredits(org.creditLimit, 'en-US')}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {signupModeLabel(org.signupMode)}
                    {org.signupMode === 'domain_restricted' && org.allowedEmailDomain && (
                      <span className="ml-1 text-xs text-muted-foreground/70">
                        ({org.allowedEmailDomain})
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{formatDate(org.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateOrgDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onCreated={(orgId) => {
          setIsCreateOpen(false)
          void navigate({
            to: '/admin/organizations/$orgId',
            params: { orgId },
          })
        }}
      />
    </div>
  )
}
